// TagPill — 단일 태그 표시 컴포넌트.
// 색상 점(color dot) + 이름 + 선택적 제거 버튼으로 구성.
// onClick: 태그 클릭 시 Explorer에서 해당 태그 필터로 이동.
// onRemove: X 버튼 클릭 시 태그 제거 (TagChipInput 내부에서 사용).
// hover 시에만 X 버튼 노출, 둥근 원형 아이콘 버튼으로 표시.

import { X } from 'lucide-react';
import type { Tag } from '../api/tags';

// ─── 타입 ────────────────────────────────────────────────

export interface TagPillProps {
  /** 표시할 태그 — id, name, color 포함 */
  tag: Tag;
  /** 클릭 핸들러 (Explorer 태그 필터 이동 등) */
  onClick?: (tag: Tag) => void;
  /** 제거 버튼 클릭 핸들러 (TagChipInput 내 X 버튼) */
  onRemove?: (tag: Tag) => void;
  /** 칩 크기 (기본 md) */
  size?: 'sm' | 'md';
  /** 제거 중인지 여부 (disabled 상태) */
  removing?: boolean;
}

// ─── 색상 폴백 팔레트 (color null인 경우) ──────────────

const FALLBACK_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4',
];

/** 태그 이름을 팔레트 인덱스로 해시 */
function hashColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  }
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length];
}

// ─── TagPill 컴포넌트 ────────────────────────────────────

export function TagPill({ tag, onClick, onRemove, size = 'md', removing = false }: TagPillProps) {
  const dotColor = tag.color ?? hashColor(tag.name);
  const sizeClass = size === 'sm' ? 'tag-pill--sm' : '';
  const clickable = !!onClick;

  const handleClick = (e: React.MouseEvent) => {
    if (!onClick) return;
    e.stopPropagation();
    onClick(tag);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(tag);
  };

  return (
    <span
      className={`tag-pill${sizeClass ? ` ${sizeClass}` : ''}${clickable ? ' tag-pill--clickable' : ''}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(tag);
        }
      } : undefined}
      aria-label={`태그: ${tag.name}`}
      title={tag.name}
    >
      {/* 색상 점 */}
      <span
        className="tag-pill__dot"
        style={{ backgroundColor: dotColor }}
        aria-hidden="true"
      />
      {/* 이름 */}
      <span className="tag-pill__name">{tag.name}</span>
      {/* 제거 버튼 — hover 시에만 노출, 둥근 원형 미니 아이콘 버튼 */}
      {onRemove && (
        <button
          type="button"
          className="tag-pill__remove"
          onClick={handleRemove}
          disabled={removing}
          aria-label={`${tag.name} 태그 제거`}
          title="태그 제거"
        >
          <X size={10} strokeWidth={2.5} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
